import { useEffect, useState } from 'react';
import { inferInitialLoadMode, MediaFileType } from '../../utils/media';
import { ossImage } from '../../utils/ossImage';

type LoadMode = 'oss' | 'raw' | 'video';

const secureUrl = (url: string) => url.replace(/^http:/, 'https:');

export const AssignmentMediaPreview = ({
  url,
  thumbnail = true,
  mediaTypeHint,
  embedded = false,
}: {
  url: string;
  thumbnail?: boolean;
  mediaTypeHint?: MediaFileType;
  /** 嵌入固定尺寸容器（如成绩单网格），object-contain 且不撑开 min-h */
  embedded?: boolean;
}) => {
  const [loadMode, setLoadMode] = useState<LoadMode>(() =>
    inferInitialLoadMode(url, mediaTypeHint),
  );

  useEffect(() => {
    setLoadMode(inferInitialLoadMode(url, mediaTypeHint));
  }, [url, mediaTypeHint]);

  const handleImageError = () => {
    setLoadMode((mode) => {
      if (mode === 'oss') {
        return 'raw';
      }
      if (mode === 'raw') {
        return 'video';
      }
      return mode;
    });
  };

  const handleVideoError = () => {
    setLoadMode((mode) => (mode === 'video' ? 'oss' : mode));
  };

  const imageSrc =
    loadMode === 'raw'
      ? secureUrl(url)
      : ossImage(
          url,
          thumbnail ? { resize: { w: 240, h: 240, m: 'fill' } } : { resize: { w: 1200 } },
        );

  const objectClass = embedded
    ? 'max-h-full max-w-full object-contain object-center'
    : thumbnail
      ? 'object-cover'
      : 'object-contain object-center';

  const media =
    loadMode === 'video' ? (
      <video
        className={
          embedded || !thumbnail
            ? `max-h-full max-w-full ${objectClass}`
            : `absolute inset-0 h-full w-full ${objectClass}`
        }
        src={thumbnail ? `${secureUrl(url)}#t=0.1` : secureUrl(url)}
        controls={!thumbnail}
        controlsList="nodownload nofullscreen"
        muted={thumbnail}
        playsInline
        preload="metadata"
        onContextMenu={(e) => e.preventDefault()}
        onError={handleVideoError}
      />
    ) : (
      <img
        className={
          embedded || !thumbnail
            ? `max-h-full max-w-full ${objectClass}`
            : `absolute inset-0 h-full w-full ${objectClass}`
        }
        src={imageSrc}
        alt=""
        draggable={false}
        onError={handleImageError}
      />
    );

  const preview = (
    <div
      className={
        embedded
          ? 'relative flex h-full w-full items-center justify-center overflow-hidden'
          : thumbnail
            ? 'absolute inset-0 overflow-hidden'
            : 'relative flex h-full min-h-[60vh] w-full items-center justify-center overflow-hidden'
      }
    >
      {media}
    </div>
  );

  return preview;
};
